import type { BetCurrencyLevel } from "@/services/bet";
import request from "@/utils/request";

export interface MerchantItem {
  id: number;
  appid: string;
  publicKey: string;
  endPoint: string;
  createdAt: number;
  operatorName: string;
  operatorManagerName: string;
  pOperatorName: string;
  contactInformation: string;
  userName: string;
  isOpenGoogleCheck: 0 | 1;
  remark: string;
  status: 0 | 1;
  walletMode: "0" | "1";
  timeZone: string;
  fee: number;
  agentLevel: number;
  pid: number;
  groupDailySwitch?: number;
  bindTime: number;
  unbindTime: number;
  currencyRatio?: string;
  hallSwitch?: boolean;
  children?: MerchantItem[];
  childrenCount?: number;
  region?: string;
  forceSettlement?: string;
  // 以下为兼容旧代码的字段
  merchantNo?: string;
  name?: string;
  manager?: string;
  contact?: string;
  parentId?: number;
  parentName?: string;
}

export interface LoginOperatorParams {
  id: number;
  verifyToken: string;
}

export interface LoginOperatorResponse {
  loginVerifyToken: string;
}

export interface MerchantDetail {
  /**
   * 代理层级 2一级代理 3二级代理 4普通商户
   */
  agentLevel: number;
  /**
   * 商户唯一标识
   */
  appid: string;
  /**
   * 绑定上级时间
   */
  bindTime: number;
  /**
   * 联系方式
   */
  contactInformation: string;
  /**
   * 创建时间
   */
  createdAt: number;
  /**
   * 商户服务地址
   */
  endPoint: string;
  /**
   * 手续费 分成比例 x%
   */
  fee: number;
  /**
   * 谷歌身份验证秘钥
   */
  googleSecret: string;
  /**
   * 谷歌身份验证秘钥url
   */
  googleUrl: string;
  /**
   * 主键id
   */
  id: number;
  /**
   * ip列表
   */
  ipList: string;
  /**
   * 是否打开谷歌身份验证1是开启0是默认
   */
  isOpenGoogleCheck: number;
  /**
   * 登录ip
   */
  loginIp: string;
  /**
   * 上次登录时间
   */
  loginTime: number;
  /**
   * 负责人
   */
  operatorManagerName: string;
  /**
   * 商户名称
   */
  operatorName: string;
  /**
   * 上级代理的id 0为没有上级/上级是平台
   */
  pid: number;
  /**
   * 商户公钥
   */
  publicKey: string;
  /**
   * 备注
   */
  remark: string;
  /**
   * 0正常,1冻结
   */
  status: number;
  /**
   * 时区
   */
  timeZone: string;
  /**
   * 用户的token
   */
  token: string;
  /**
   * 解绑时间
   */
  unbindTime: number;
  /**
   * 更新时间
   */
  updatedAt: number;
  /**
   * 用户名
   */
  userName: string;
  /**
   * 0=单钱包模式,1=转账模式
   */
  walletMode: string;
  /**
   * 群日报开关
   */
  groupDailySwitch?: boolean;
  /**
   * 自动结单开关
   */
  forceSettlement?: number;
  /**
   * 服务器地区
   */
  region?: string;
  [property: string]: any;
}

export interface GetOperatorListParams {
  keywords?: string;
  pid?: string;
}

export interface OperatorItem {
  id: number;
  appid: string;
  operatorName: string;
  operatorManagerName: string;
  contactInformation: string;
  status: number;
  pid: number;
  parentName?: string;
  agentLevel: number;
  createdAt: number;
  updatedAt: number;
}

/** 获取商户列表 GET /api/v1/sys/listOperator */
export async function getOperatorList(params: GetOperatorListParams) {
  return request<{ list: OperatorItem[]; total: number }>(
    "/api/v1/sys/listOperator",
    {
      method: "GET",
      params,
    },
  );
}

/** 获取商户详情 GET /api/v1/sys/getOperator */
export async function getOperator(params: { id: number }) {
  return request<MerchantDetail>("/api/v1/sys/getOperator", {
    method: "GET",
    params,
  });
}

export type MerchantDataItem = MerchantItem;

export interface OperatorCurrencyRatioItem {
  /**
   * 货币编码
   */
  code: string;
  /**
   * 比例
   */
  ratio: number;
  [property: string]: unknown;
}

export interface OperatorCurrencyRatioData {
  appid: string;
  /**
   * 货币比例
   */
  currencyRatios: OperatorCurrencyRatioItem[];
  id: number;
  operatorName: string;
  /**
   * 其它商户
   */
  otherMerchants: string[];
  [property: string]: unknown;
}

export interface GameRiskThresholdConfig {
  threshold?: number;
  reSpinTimes?: number;
  moreInfo?: string;
  newPlayerThreshold?: string | number;
  otherMerchants?: Array<{
    appid: string;
    operatorName?: string;
    pOperatorName?: string;
  }>;
}

export interface OperatorGameNewPlayerConfig {
  jackpot?: number;
  firstCron?: string;
  secondCron?: string;
  subRtp?: number;
  minSubScore?: number;
  gtSetJackpotScore?: number;
  ltSetJackpotScore?: number;
  trigJackpotPercent?: number;
  trigHighRtpPercent?: number;
  maxSpin?: number;
  maxWin?: number;
  singleScore?: number;
  otherMerchants?: Array<{
    appid: string;
    operatorName?: string;
    pOperatorName?: string;
  }>;
  reviewStatus?: number;
  [property: string]: unknown;
}

export interface MerchantGameBetLevelDraft {
  betLevelConfigId?: number | string;
  betLevelName?: string;
  id?: number | string;
  name?: string;
  [property: string]: unknown;
}

export interface MerchantGameBetLevelInfo {
  betLevelConfigId?: number | string;
  betLevelName?: string;
  currencyLevels?: BetCurrencyLevel[] | string | null;
  changeLevelDraft?: MerchantGameBetLevelDraft | string | null;
  /**
   * 0-无变更，1-审核中，2-已驳回
   */
  changeLevelStatus?: number;
}

export interface MerchantGameConfigItem {
  gameId: number;
  gameName: string;
  gameType: number | string;
  supportLanguage: string[] | string;
  supportCurrency: string[] | string;
  status: 0 | 1;
  betLevelInfo?: MerchantGameBetLevelInfo;
  /** RTP调权开关：0关闭 1开启 */
  rtpAdjust?: number;
  /** 当前游戏RTP值 */
  rtp?: number;
  /** 风控配置信息 */
  riskConfigInfo?: {
    threshold?: number;
    reSpinTimes?: number;
    newPlayerThreshold?: number | string;
    [property: string]: unknown;
  };
  /** 新手奖池配置信息 */
  newPlayerConfigInfo?: {
    jackpot?: number;
    remainJackpot?: number;
    [property: string]: unknown;
  };
}

export interface GetGameConfigResponse {
  list: MerchantGameConfigItem[];
}

export interface GameHallActivity {
  activityId?: number;
  activityType?: number;
  badge?: number;
  endTime?: number;
  gameId?: number;
  gameName?: string;
  imgUrl?: string;
  name?: string;
  startTime?: number;
  totalEndTime?: number;
  visible?: boolean;
  weight?: number;
  [property: string]: any;
}

export interface GameHallGame {
  badge?: number;
  gameId?: number;
  gameName?: string;
  imgUrl?: string;
  visible?: boolean;
  weight?: number;
  [property: string]: any;
}

export interface GameHallConfigData {
  activities: GameHallActivity[];
  activitySwitch: boolean;
  appid: string;
  games: GameHallGame[];
  gameSwitch: boolean;
  hallSwitch: boolean;
  reviewStatus: number;
  [property: string]: any;
}

export interface GameHallConfigPayload {
  activitySwitch: boolean;
  appid: string;
  config: {
    activities: Array<{
      activityId: number;
      activityType: number;
      badge: number;
      visible: boolean;
      weight: number;
    }>;
    games: Array<{
      badge: number;
      gameId: number;
      visible: boolean;
      weight: number;
    }>;
  };
  gameSwitch: boolean;
  hallSwitch: boolean;
  verifyToken: string;
  [property: string]: any;
}

/** 获取商户大厅配置 GET /api/v1/sys/getGameHallConfig */
export async function getGameHallConfig(params: { appid?: string }) {
  return request<GameHallConfigData>("/api/v1/sys/getGameHallConfig", {
    method: "GET",
    params,
  });
}

/** 保存商户大厅配置 POST /api/v1/sys/saveGameHallConfig */
export async function saveGameHallConfig(data: GameHallConfigPayload) {
  return request<any>("/api/v1/sys/saveGameHallConfig", {
    method: "POST",
    data,
  });
}

export interface UpdateGameConfigPayload {
  appid: string;
  list: { gameId: number; status: 0 | 1 }[];
  verifyToken?: string;
}

export interface GetOperatorGameRiskConfigParams {
  appid: string;
  gameId: number;
}

export interface UpdateOperatorGameRiskConfigPayload {
  appid: string;
  gameId: number;
  threshold?: number;
  reSpinTimes?: number;
  moreInfo?: string;
  newPlayerThreshold?: string | number;
  otherAppids?: string[];
  verifyToken?: string;
}

export interface GetOperatorGameNewPlayerConfigParams {
  appid: string;
  gameId: number;
}

export interface UpdateOperatorGameNewPlayerConfigPayload {
  appid: string;
  gameId: number;
  jackpot?: number;
  firstCron?: string;
  secondCron?: string;
  subRtp?: number;
  minSubScore?: number;
  gtSetJackpotScore?: number;
  ltSetJackpotScore?: number;
  trigJackpotPercent?: number;
  trigHighRtpPercent?: number;
  maxSpin?: number;
  maxWin?: number;
  singleScore?: number;
  otherAppids?: string[];
  verifyToken?: string;
}

export interface UpdateOperatorCurrencyRatiosPayload {
  /**
   * 货币比例
   */
  currencyRatios: OperatorCurrencyRatioItem[];
  id: number;
  /**
   * 其它商户
   */
  otherAppids: string[];
  verifyToken?: string;
  [property: string]: unknown;
}

export interface UpdateOperatorGameBetLevelPayload {
  appid: string;
  gameId: number;
  betLevelConfigId: number;
  verifyToken?: string;
}

export interface GetGameRtpParams {
  appid?: string;
  gameId?: number;
  [property: string]: unknown;
}

export interface GameRtpOtherMerchant {
  appid: string;
  operatorName: string;
  [property: string]: unknown;
}

export interface GameRtpWeightItem {
  name: string;
  rtp: number;
  rtpConfigId: number;
  rtpType: string;
  weight: number;
  [property: string]: unknown;
}

export interface GameRtpData {
  appid: string;
  approvalStatus: number;
  gameId: number;
  gameName: string;
  operatorName: string;
  otherMerchants: GameRtpOtherMerchant[];
  overallRtp: number;
  reviewComments?: string;
  rtpWeight: GameRtpWeightItem[];
  [property: string]: unknown;
}

export interface UpdateGameRtpWeightPayload {
  rtpConfigId: number | null;
  rtpType: string;
  weight: number;
}

export interface UpdateGameRtpSubmitPayload {
  otherAppids: string[];
  rtpMathVersion: number;
  rtpWeight: UpdateGameRtpWeightPayload[];
  /** 无权重模式下的RTP配置ID（当rtpAdjust=0时使用） */
  rtpNoWeightConfigId?: number;
  verifyToken?: string;
}

export interface UpdateGameRtpPayload extends UpdateGameRtpSubmitPayload {
  appid: string;
  gameId: number;
  [property: string]: unknown;
}

export interface GetOperatorTreeParams {
  /**
   * 创建时间结束
   */
  createdAtE?: number;
  /**
   * 创建时间开始
   */
  createdAtS?: number;
  /**
   * 商户搜索ID或名称
   */
  keyword?: string;
  /**
   * 钱包类型 0-单钱包 1-转账
   */
  walletMode?: string;
}

/** 获取商户树形列表 GET /api/v1/sys/listOperatorTree */
export async function listOperatorTree(params: GetOperatorTreeParams) {
  return request<{ list: MerchantItem[] }>("/api/v1/sys/listOperatorTree", {
    method: "GET",
    params,
  });
}

/** 导出商户 CSV GET /api/v1/sys/listOperatorTreeCsv */
export async function exportOperatorTreeCsv(
  params: GetOperatorTreeParams,
  options?: { [key: string]: any },
) {
  return request<any>("/api/v1/sys/listOperatorTreeCsv", {
    method: "GET",
    params,
    responseType: "blob",
    ...(options || {}),
  });
}

export interface GetMerchantListParams {
  startTime?: number;
  endTime?: number;
  keywords?: string; // 搜索商户ID或名称
  level?: number;
  walletType?: number;
  page?: number;
  size?: number;
}

export interface CreateMerchantParams {
  operatorName?: string;
  operatorManagerName?: string;
  contactInformation?: string;
  username?: string; // 登录账号
  password?: string; // 登录密码
  pid?: number; // 上级
  walletMode?: string;
  fee?: number;
  endPoint?: string;
  ipList?: string;
  remark?: string;
  status?: number;
  agentLevel?: number;
  timeZone?: string;
  id?: number;
  verifyToken?: string;
  isOpenGoogleCheck?: boolean;
  publicKey?: string;
  groupDailySwitch?: number;
  currencyRatio?: number | string;
  region?: string;
  /** 手动结单 0关闭 1开启 */
  forceSettlement?: string;
}

export interface CreateMerchantResponse {
  id?: number;
  appid?: string;
  verifyToken?: string;
  data?: {
    id?: number;
    appid?: string;
    verifyToken?: string;
  };
  operator?: {
    id?: number;
    appid?: string;
    verifyToken?: string;
  };
}

export interface ApplyCreateOperatorParams extends CreateMerchantParams {
  /**
   * 申请单ID，编辑草稿时必传
   */
  id?: number;
  /**
   * 操作：1保存 2保存并提交
   */
  action: number;
  /**
   * 开通商户游戏
   */
  openOperatorGame?: OpenOperatorGamePayload;
  [property: string]: any;
}

export interface OpenOperatorGamePayload {
  list: Array<{
    gameId: number;
    status: number;
  }>;
}

/** 获取商户列表 GET /api/v1/merchant/list */
export async function getMerchantList(params: GetMerchantListParams) {
  return request<{ list: MerchantDataItem[]; total: number }>(
    "/api/v1/merchant/list",
    {
      method: "GET",
      params,
    },
  );
}

/** 添加商户 POST /api/v1/sys/createOperator */
export async function createMerchant(data: CreateMerchantParams) {
  return request<CreateMerchantResponse>("/api/v1/sys/createOperator", {
    method: "POST",
    data,
  });
}

/** 新增商户/代理申请 POST /api/v1/sys/applyCreateOperator */
export async function applyCreateOperator(data: ApplyCreateOperatorParams) {
  return request<any>("/api/v1/sys/applyCreateOperator", {
    method: "POST",
    data,
  });
}

/** 登录商户/代理 POST /api/v1/sys/loginOperator */
export async function loginOperator(data: LoginOperatorParams) {
  return request<LoginOperatorResponse>("/api/v1/sys/loginOperator", {
    method: "POST",
    data,
  });
}

export interface UpdateMerchantParams extends CreateMerchantParams {
  id: number;
  reason?: string;
}

/** 更新商户/代理 POST /api/v1/sys/updateOperator */
export async function updateMerchant(data: UpdateMerchantParams) {
  return request<any>("/api/v1/sys/updateOperator", {
    method: "POST",
    data,
  });
}

/** 删除商户 POST /api/v1/merchant/delete */
export async function deleteMerchant(data: { id: number }) {
  return request<any>("/api/v1/merchant/delete", {
    method: "POST",
    data,
  });
}

/** 重置公钥 POST /api/v1/merchant/resetKey */
export async function resetPublicKey(data: { id: number }) {
  return request<any>("/api/v1/merchant/resetKey", {
    method: "POST",
    data,
  });
}

/** 重置密码 POST /api/v1/merchant/resetPwd */
export async function resetPassword(data: { id: number }) {
  return request<any>("/api/v1/merchant/resetPwd", {
    method: "POST",
    data,
  });
}

/** 重置谷歌验证 POST /api/v1/merchant/resetGoogle */
export async function resetGoogleAuth(data: { id: number }) {
  return request<any>("/api/v1/merchant/resetGoogle", {
    method: "POST",
    data,
  });
}

/** 解除上级关系 POST /api/v1/merchant/unbindParent */
export async function unbindParent(data: {
  id: number;
  reason: string;
  googleTicket: string;
}) {
  return request<any>("/api/v1/merchant/unbindParent", {
    method: "POST",
    data,
  });
}

/** 删除商户/代理 POST /api/v1/sys/delOperator */
export async function delOperator(data: {
  id: number;
  verifyToken: string;
  reason: string;
}) {
  return request<any>("/api/v1/sys/delOperator", {
    method: "POST",
    data,
  });
}

/** 获取商户游戏配置 GET /api/v1/sys/listOperatorGame */
export async function getGameConfig(params: { appid: string; all: boolean }) {
  return request<GetGameConfigResponse>("/api/v1/sys/listOperatorGame", {
    method: "GET",
    params,
  });
}

/** 获取商户货币比例 GET /api/v1/sys/getOperatorRatios */
export async function getOperatorRatios(params: { id: number }) {
  return request<OperatorCurrencyRatioData>("/api/v1/sys/getOperatorRatios", {
    method: "GET",
    params,
  });
}

/** 更新商户货币比例 POST /api/v1/sys/updateOperatorCurrencyRatios */
export async function updateOperatorCurrencyRatios(
  data: UpdateOperatorCurrencyRatiosPayload,
) {
  return request<any>("/api/v1/sys/updateOperatorCurrencyRatios", {
    method: "POST",
    data,
  });
}

/** 更新游戏配置 POST /api/v1/sys/updateOperatorGame */
export async function updateGameConfig(data: UpdateGameConfigPayload) {
  return request<any>("/api/v1/sys/updateOperatorGame", {
    method: "POST",
    data,
  });
}

/** 获取游戏风控详情 GET /api/v1/sys/getOperatorGameRiskConfig */
export async function getOperatorGameRiskConfig(
  params: GetOperatorGameRiskConfigParams,
) {
  return request<GameRiskThresholdConfig>(
    "/api/v1/sys/getOperatorGameRiskConfig",
    {
      method: "GET",
      params,
    },
  );
}

/** 更新游戏风控配置 POST /api/v1/sys/updateOperatorGameRiskConfig */
export async function updateOperatorGameRiskConfig(
  data: UpdateOperatorGameRiskConfigPayload,
) {
  return request<any>("/api/v1/sys/updateOperatorGameRiskConfig", {
    method: "POST",
    data,
  });
}

/** 获取商户游戏新手奖池配置 GET /api/v1/sys/getOperatorGameNewPlayerConfig */
export async function getOperatorGameNewPlayerConfig(
  params: GetOperatorGameNewPlayerConfigParams,
) {
  return request<OperatorGameNewPlayerConfig>(
    "/api/v1/sys/getOperatorGameNewPlayerConfig",
    {
      method: "GET",
      params,
    },
  );
}

/** 更新商户游戏新手奖池配置 POST /api/v1/sys/updateOperatorGameNewPlayerConfig */
export async function updateOperatorGameNewPlayerConfig(
  data: UpdateOperatorGameNewPlayerConfigPayload,
) {
  return request<any>("/api/v1/sys/updateOperatorGameNewPlayerConfig", {
    method: "POST",
    data,
  });
}

/** 更新商户游戏档位 POST /api/v1/sys/updateOperatorGameBetLevel */
export async function updateOperatorGameBetLevel(
  data: UpdateOperatorGameBetLevelPayload,
) {
  return request<any>("/api/v1/sys/updateOperatorGameBetLevel", {
    method: "POST",
    data,
  });
}

/** 获取游戏 RTP 配置 GET /api/v1/sys/getGameRtp */
export async function getGameRtp(params: GetGameRtpParams) {
  return request<GameRtpData>("/api/v1/sys/getGameRtp", {
    method: "GET",
    params,
  });
}

/** 更新游戏 RTP 配置 POST /api/v1/sys/updateGameRtp */
export async function updateGameRtp(data: UpdateGameRtpPayload) {
  return request<any>("/api/v1/sys/updateGameRtp", {
    method: "POST",
    data,
  });
}

/** 获取层级树（用于添加商户时的上级选择） GET /api/v1/merchant/levelTree */
export async function getLevelTree() {
  return request<any[]>("/api/v1/merchant/levelTree", {
    method: "GET",
  });
}

/** 批量导入商户 POST /api/v1/sys/uploadOperator */
export async function uploadMerchants(
  data: FormData,
  options?: { [key: string]: any },
) {
  return request<any>("/api/v1/sys/uploadOperator", {
    method: "POST",
    data,
    headers: {
      "Content-Type": "multipart/form-data",
    },
    ...(options || {}),
  });
}

export interface BatchUpdateMerchantStatusParams {
  ids: number[];
  /** 0正常, 1冻结 */
  status: 0 | 1;
  verifyToken: string;
}

/** 批量修改商户状态 POST /api/v1/sys/batchOperatorStatus */
export async function batchUpdateMerchantStatus(
  data: BatchUpdateMerchantStatusParams,
) {
  return request<any>("/api/v1/sys/batchOperatorStatus", {
    method: "POST",
    data,
  });
}

/** 获取未绑定的商户列表 GET /api/v1/sys/listUnbindOperator */
export async function listUnbindOperator(params: { keywords?: string }) {
  return request<{
    list: {
      id: number;
      appid: string;
      operatorName: string;
      operatorManagerName: string;
      contactInformation: string;
    }[];
  }>("/api/v1/sys/listUnbindOperator", {
    method: "GET",
    params,
  });
}

/** 批量绑定商户 POST /api/v1/sys/bindOperator */
export async function bindOperator(data: {
  id: number;
  operatorIds: number[];
}) {
  return request<any>("/api/v1/sys/bindOperator", {
    method: "POST",
    data,
  });
}

/** 下载商户模板 GET /api/v1/merchant/template */
export async function downloadMerchantTemplate() {
  return request<any>("/api/v1/merchant/template", {
    method: "GET",
    responseType: "blob",
  });
}

export interface UserDataItem {
  /** 唯一ID */
  id: number;
  /** 商户appid */
  appid: string;
  /** 用户角色ID */
  roleId: string;
  isWhite: boolean;
  /** 币种 */
  currency: string;
  /** 累计下注金额 */
  betAmount: number;
  /** 剩余分数/余额 */
  balanceAmount: number;
  /** 下注次数 */
  betCount: number;
  /** 注册时间 (秒级时间戳) */
  registerTime: number;
  /** 最近登录时间 (秒级时间戳) */
  lastLoginTime: number;
  /** 注册IP */
  registerIp: string;
  /** 一级代理名称 */
  agent1OperatorName: string;
  /** 二级代理名称 */
  agent2OperatorName: string;
  /** 商户操作员ID */
  operatorId: number;
  /** 商户名称 */
  operatorName: string;
  /** 盈亏 */
  winAmount: number;
  /** 赢钱金额 */
  scoreAmount: number;
  /** 活动彩金 */
  freeSpinAmount: number;
}

export interface UserLoginLogItem {
  /** 唯一ID */
  id: number;
  /** 游戏ID */
  gameId: number;
  /** 商户appid */
  appid: string;
  /** 用户角色ID */
  roleId: string;
  /** 登录时间 (秒级时间戳) */
  loginTime: number;
  /** 登录IP */
  loginIp: string;
}

export interface GetUserLoginLogParams {
  page?: string;
  roleId?: string;
  size?: string;
}

/** 获取用户登录日志 GET /api/v1/sys/listGameUserLoginLog */
export async function getUserLoginLog(params: GetUserLoginLogParams) {
  return request<{ list: UserLoginLogItem[]; total: number }>(
    "/api/v1/sys/listGameUserLoginLog",
    {
      method: "GET",
      params,
    },
  );
}

export interface GetUserListParams {
  /** 商户appids，多个用,分隔 */
  appids?: string;
  /** 结束时间 */
  endTime?: string;
  /** ip */
  ip?: string;
  /** 当前页码 */
  page?: string;
  /** 用户ID */
  roleId?: string;
  /** 每页数量 */
  size?: string;
  /** 开始时间 */
  startTime?: string;
  /** 排序字段: winAmount, balanceAmount, betAmount, betCount */
  orderField?: string;
  /** 排序类型: desc 降序, asc 升序 */
  orderType?: "desc" | "asc" | "";
  [property: string]: any;
}

/** 获取用户管理列表 GET /api/v1/sys/listGameUser */
export async function getUserList(params: GetUserListParams) {
  return request<{ list: UserDataItem[]; total: number }>(
    "/api/v1/sys/listGameUser",
    {
      method: "GET",
      params,
    },
  );
}

/** �̻�������פ�б��� */
export interface OperatorApplyItem {
  agentLevel: number;
  appid: string;
  applyAdminId: number;
  applyAdminName: string;
  applyAdminOperatorAppid: string;
  applyAdminOperatorName: string;
  applyAdminUserName: string;
  applyTime?: number;
  contactInformation: string;
  createdAt: number;
  draft: string;
  endPoint: string;
  fee: number | number;
  groupDailySwitch: number;
  handler: number;
  handlerName: string;
  id: number;
  ipList: string;
  openOperatorGame: string | OpenOperatorGamePayload;
  operatorId: number;
  operatorManagerName: string;
  operatorName: string;
  parentOperatorAppid: string;
  parentOperatorName: string;
  pid: number;
  processingTime: number;
  publicKey: string;
  remark: string;
  reviewComments: string;
  reviewStatus: number;
  status: number;
  timeZone: string;
  updatedAt: number;
  userName: string;
  walletMode: string;
}

/** ��ȡ�̻������б�������� */
export interface GetOperatorApplyParams {
  endTime?: string;
  keywords?: string;
  page?: string;
  reviewStatus?: string;
  size?: string;
  startTime?: string;
  walletMode?: string;
}

/** ��ȡ�̻������б� GET /api/v1/sys/listOperatorApply */
export async function getOperatorApplyList(params: GetOperatorApplyParams) {
  return request<{ list: OperatorApplyItem[]; totalCount: number }>(
    "/api/v1/sys/listOperatorApply",
    {
      method: "GET",
      params,
    },
  );
}

export interface ReviewOperatorApplyParams {
  id: number;
  reviewComments: string;
}

/** 审核通过商户申请 POST /api/v1/sys/approveOperatorApply */
export async function approveOperatorApply(data: ReviewOperatorApplyParams) {
  return request<unknown>("/api/v1/sys/approveOperatorApply", {
    method: "POST",
    data,
  });
}

/** 审核拒绝商户申请 POST /api/v1/sys/rejectOperatorApply */
export async function rejectOperatorApply(data: ReviewOperatorApplyParams) {
  return request<unknown>("/api/v1/sys/rejectOperatorApply", {
    method: "POST",
    data,
  });
}

/** 发布商户申请 POST /api/v1/sys/publishOperatorApply */
export async function publishOperatorApply(data: {
  id: number;
  verifyToken: string;
}) {
  return request<unknown>("/api/v1/sys/publishOperatorApply", {
    method: "POST",
    data,
  });
}

/** 强制定时结算列表请求参数 */
export interface ListForceSettlementParams {
  /** 商户筛选 */
  appid?: string;
  /** 2审核中 3已发布 4已拒绝 31通过待执行 32已执行 */
  approvalStatus?: string;
  /** 结束时间 */
  endTime?: string[];
  /** 游戏筛选 */
  gameId?: string;
  page?: string;
  /** 订单 */
  parentId?: string;
  size?: string;
  /** 开始时间 */
  startTime?: string[];
  [property: string]: any;
}

/** 强制定时结算 - 订单信息 */
export interface ForceSettlementOrder {
  bet: number;
  betLevel: number;
  buyBonusType: number;
  configVersion: string;
  countryCode: string;
  createDatetime: number;
  currencyCode: string;
  currencyMult: number;
  exchangeRate: number;
  gameId: number;
  geonameId: number;
  ip: string;
  isMerchantFs: number;
  isPlatformFs: number;
  isSevenFs: number;
  mathVersion: string;
  operatorToken: string;
  orderStatus: number;
  parentId: number;
  receiptId: string;
  region: string;
  roleId: string;
  roundEnd: number;
  score: number;
  status: number;
  subId: number;
  surplusCoin: number;
  [property: string]: any;
}

/** 强制定时结算列表项 */
export interface ForceSettlementItem {
  adminId?: number;
  adminName?: string;
  appid?: string;
  approvalStatus?: number;
  canRetry?: boolean;
  createdAt?: number;
  executedByListSync?: boolean;
  gameId?: number;
  hasRoundEnd?: boolean;
  id?: number;
  merchantId?: string;
  order?: ForceSettlementOrder;
  parentId?: number;
  pushAt?: number;
  redisMember?: string;
  remark?: string;
  roleId?: string;
  status?: number;
  updatedAt?: number;
  [property: string]: any;
}

/** 获取强制定时结算列表 GET /api/v1/sys/listForceSettlement */
export async function listForceSettlement(params: ListForceSettlementParams) {
  return request<{ list: ForceSettlementItem[]; totalCount: number }>(
    "/api/v1/sys/listForceSettlement",
    {
      method: "GET",
      params,
    },
  );
}

/** 创建手动结算请求参数 */
export interface CreateForceSettlementParams {
  /** 订单id */
  parentId: number;
  remark: string;
  /** 谷歌验证成功后获得的票据 */
  verifyToken: string;
  [property: string]: any;
}

/** 创建手动结算 POST /api/v1/sys/createForceSettlement */
export async function createForceSettlement(
  data: CreateForceSettlementParams,
) {
  return request<unknown>("/api/v1/sys/createForceSettlement", {
    method: "POST",
    data,
  });
}

/** 重试强制定时结算请求参数 */
export interface RetryForceSettlementParams {
  /** 结单表id */
  id: number;
  [property: string]: any;
}

/** 重试强制定时结算 POST /api/v1/sys/retryForceSettlement */
export async function retryForceSettlement(data: RetryForceSettlementParams) {
  return request<unknown>("/api/v1/sys/retryForceSettlement", {
    method: "POST",
    data,
  });
}
